import { ArrowRight, ClipboardCheck, Gavel, ScrollText } from 'lucide-react'
import { Link } from 'react-router-dom'

import { ctaClicked } from '@/lib/analytics'
import { Button } from '@/components/ui/button'
import { SectionLabel } from '@/features/landing/components/SectionLabel'
import { PhoneFrame } from '@/features/landing/sections/MobileFirst'
import {
  TIER_MEMBER_CAP,
  TIER_MONTHLY_PRICE,
} from '@/lib/squadron/tierMeta'

/**
 * The clan-leader band — the landing-side twin of the outbound Leaders Pack
 * (e404_infra/marketing/LEADERS_PACK). The pack's email CTA lands on this
 * page, so the page has to receive that reader: someone who already runs a
 * community and is deciding whether e404 lightens the load or adds to it.
 *
 * Voice matches the pack: commander brief — peer-to-peer, operational. The
 * bracket screen is the populated Ironwake mockup (fictional data), the same
 * asset the pack and email use, so the click-through feels continuous.
 */

const PROOFS = [
  {
    icon: Gavel,
    title: 'Disputes route to you — on the record',
    body: 'Both captains confirm the score or the match flags DISPUTED and lands on your desk, with the event thread holding the argument in one place. Your override is audit-logged.',
  },
  {
    icon: ClipboardCheck,
    title: 'The chasing is automated',
    body: 'RSVPs, check-ins, lock-in countdowns and result nudges are push notifications, not your evening. You set the event; the app does the door-knocking.',
  },
  {
    icon: ScrollText,
    title: 'The money is legible',
    body: 'Optional member dues under your name via Stripe Connect (Bronze and up). You see the exact split — VAT, card processing, e404’s 0.4% — before you set a fee.',
  },
]

export function ForClanLeaders() {
  return (
    <section
      id="for-leaders"
      // scroll-mt clears the sticky Nav when the Leaders Pack email deep-links
      // straight to /#for-leaders — without it the header clips the section top.
      className="relative scroll-mt-20 overflow-hidden border-b border-border/60 px-6 py-24 md:py-32"
    >
      {/* Command-room wash — one directional sweep, no confetti. */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-40"
        style={{
          background:
            'linear-gradient(115deg, transparent 0%, color-mix(in srgb, var(--color-primary) 8%, transparent) 35%, transparent 60%), radial-gradient(ellipse 50% 60% at 85% 50%, color-mix(in srgb, var(--color-tertiary) 7%, transparent), transparent 70%)',
        }}
      />

      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
        <div className="flex flex-col items-start gap-6">
          <SectionLabel index="04" label="For the one who runs it" />
          <h2 className="font-display max-w-xl text-4xl leading-[1.05] font-semibold text-balance md:text-6xl">
            You already run the clan.{' '}
            <span className="gradient-text">This is the ops layer.</span>
          </h2>
          <p className="max-w-xl text-base text-fg-muted text-pretty md:text-lg">
            The roster, the tournaments, the dues, the argument about who won — it
            all lands on you. e404 exists so running the clan stops being a
            second job: post the game night tonight, run the cup at the
            weekend, and watch the final like everyone else.
          </p>

          <ul className="flex flex-col gap-4">
            {PROOFS.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-4">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/30 bg-primary/10 text-primary">
                  <Icon className="size-4" strokeWidth={1.75} aria-hidden />
                </span>
                <div>
                  <h3 className="font-display font-semibold tracking-tight text-fg">
                    {title}
                  </h3>
                  <p className="mt-1 text-sm text-fg-muted text-pretty">
                    {body}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <Button asChild variant="primary" size="lg">
              <Link
                to="/signup"
                onClick={() =>
                  ctaClicked('stand_up_squadron', 'for_clan_leaders', '/signup')
                }
              >
                Stand up your squadron
                <ArrowRight className="size-4" aria-hidden />
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              size="lg"
              className="font-display text-fg-muted hover:text-fg"
            >
              <a href="#how">See how it works</a>
            </Button>
          </div>
          {/* Tier facts come from tierMeta (the same source Pricing renders
              from) so this footnote can't drift from the cards one scroll
              below. */}
          <p className="text-xs text-fg-subtle">
            Free up to {TIER_MEMBER_CAP.FREE} members — game nights from day
            one. Tournaments unlock on Bronze (£
            {TIER_MONTHLY_PRICE.LEVEL_1}/mo).
          </p>
        </div>

        {/* The tournament refereeing itself — the populated bracket mockup. */}
        <div className="relative flex items-center justify-center">
          <div
            aria-hidden
            className="absolute -inset-16 -z-10 opacity-25 blur-3xl"
            style={{ background: 'var(--gradient-accent)' }}
          />
          <PhoneFrame
            src="/screenshots/bracket.png"
            alt="The e404 tournament screen mid-tournament — quarter-finals with dual-confirmed results and one disputed match routed to the host, audit trail noted."
            caption="App · Match results — demo data"
          />
        </div>
      </div>
    </section>
  )
}
